import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '../../../utils/mq';
import { useTranslation } from '../../../utils/translation';
import { useImageComponent, useLinkComponent } from '../../../utils/component';
import { DatasheetAttribute } from './types';
import { Cta } from '../../cta';
import type { LinkProps } from '../../../atoms/types';
import { MarkStars } from '../../mark-with-stars';
import Pro from '../../../icons/pro';
import Con from '../../../icons/con';
import { MainCtaInfos } from '../../types';
import { MaterialSymbol } from '../../../atoms/material-symbol';

export interface ComparativeProps {
  ctaLink?: LinkProps;
  testLink?: LinkProps;
  image: { imageId: string; alt: string };
  title?: string;
  rating?: number;
  topDataSheet?: DatasheetAttribute[];
  customDisplays?: {
    hasRating?: boolean;
    hasConclusion?: boolean;
    hasProsCons?: boolean;
    hasReviewLink?: boolean;
  };
  pros?: string[];
  cons?: string[];
  mainCtaInfos?: MainCtaInfos;
  conclusion?: any;
  notice?: string;
}

const ComparativeRow = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 var(--spacer-fluid) 0;
`;

const Wrapper = styled.div`
  background: var(--color-alt-4);
  border-radius: 10px;
  box-shadow: 0px 0px 30px 0px #0000001a;
  margin: var(--spacer-fluid) 0;
  padding: var(--spacer-fluid) var(--spacer-m-to-s);
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 0 0 20px 0 !important;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      align-items: center;
      flex-direction: row;
      gap: 30px;
    `,
  )}
`;

const ImageCard = styled.div`
  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      display: flex;
      justify-content: center;
    `,
  )}
`;

const CardScore = styled.div`
  text-align: center;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: 0 0 0 auto;
    `,
  )}
`;

const Informations = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />;

const CardContentTitle = styled.div`
  margin: 0 0 15px;
  font-family: var(--theme-typography-primary-font);
  font-size: 26px;
  font-weight: bold;
  line-height: 1.38;

  a {
    color: var(--title-foreground);
    text-decoration: none;
    transition: color 0.3s;
  }

  &:hover a {
    color: var(--theme-palette-red);
  }
`;

const Specs = styled.ul`
  list-style-type: none;
`;

const Spec = styled.li`
  font-size: 16px;
  line-height: 18px;
  margin: 0 0 10px;
  padding-left: 25px;
  position: relative;

  &:last-of-type {
    margin: 0;
  }
`;

const SpecIcon = styled.span`
  font-size: 18px;
  left: 0;
  margin: 0 12px 0 0;
  position: absolute;
  top: -1px;
`;

const Conclusion = styled.div`
  font-size: 18px;
  line-height: 27px;
`;

const Review = styled.div`
  margin: var(--spacer-m) 0 var(--spacer-m);

  a {
    color: var(--text-color-alt);
    font-size: 18px;
    font-weight: 700;
    text-decoration: none;
  }
`;

const LeftSide = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 15px;
`;

const RightSide = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 15px;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  font-size: 16px;
  gap: 12px;
  list-style: none;

  svg {
    min-width: 22px;
  }
`;

const ComparativeProsCons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      flex-direction: row;
      gap: 60px;
    `,
  )}
`;

const ProItem = styled.li`
  align-items: start;
  display: flex;
  gap: 7px;
  line-height: 22px;
`;

const ConItem = styled.li`
  align-items: start;
  display: flex;
  gap: 7px;
  line-height: 22px;
`;

const ProsConsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: 0 60px 0;
    `,
  )}
`;

const Title = styled.div`
  font-size: 16px;
  font-family: var(--theme-typography-primary-font);
  font-weight: 700;
  line-height: 1.38;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 22px;
    `,
  )}
`;

const Notice = styled.p`
  font-size: 13px;
  margin: var(--spacer-xs) 0 0;
  text-align: center;
`;

const ComparativeCard = ({
  title,
  rating,
  image,
  ctaLink,
  testLink,
  topDataSheet,
  conclusion,
  customDisplays,
  mainCtaInfos,
  pros,
  cons,
  notice,
}: ComparativeProps) => {
  const t = useTranslation();

  const ImageComponent = useImageComponent();
  const LinkComponent = useLinkComponent();

  const prosCard = useMemo(
    () =>
      topDataSheet?.map((attribute: DatasheetAttribute) => (
        <Spec className="marty-service-card__specifications-item" key={attribute.name}>
          <MaterialSymbol
            className="material-symbols-outlined"
            symbol={attribute.iconSlug}
            Component={SpecIcon}
            lazy={false}
          />
          {attribute.type === 'numeric' ? `${attribute.value} ${attribute.name.toLowerCase()}` : attribute.value}
        </Spec>
      )),
    [topDataSheet],
  );

  const imageProps = {
    ...image,
    mobileWidth: 180,
    mobileHeight: 180,
    desktopWidth: 180,
    desktopHeight: 180,
  };

  return (
    <ComparativeRow>
      <Wrapper>
        <HeaderRow>
          <ImageCard>
            <LinkComponent {...mainCtaInfos.ctaPropsCard}>
              <ImageComponent {...imageProps} />
            </LinkComponent>
          </ImageCard>
          <Informations>
            <CardContentTitle>
              {ctaLink.href ? <LinkComponent {...ctaLink}>{title}</LinkComponent> : title}
            </CardContentTitle>
            <Specs>{prosCard}</Specs>
          </Informations>
          {customDisplays.hasRating && (
            <CardScore data-testid="rating">
              <MarkStars className="mod-dark" rating={rating} max={10} />
            </CardScore>
          )}
        </HeaderRow>
        {customDisplays.hasConclusion && <Conclusion data-testid="conclusion">{conclusion}</Conclusion>}
        {testLink.href && customDisplays.hasReviewLink && (
          <Review data-testid="reviewLink">
            <LinkComponent {...testLink}>{t('design.serviceCardComparative.test', { title })}</LinkComponent>
          </Review>
        )}
        {mainCtaInfos.link && (
          <Cta {...mainCtaInfos.ctaPropsCard}>{t('design.serviceCardComparative.cta', { title })}</Cta>
        )}
        {notice && <Notice>{notice}</Notice>}
      </Wrapper>
      {customDisplays.hasProsCons && (
        <ProsConsWrapper data-testid="proCons">
          <ComparativeProsCons>
            {pros?.length > 0 ? (
              <LeftSide>
                <Title>{t('design.ratingBlock.pros')}</Title>
                <List>
                  {pros?.map((pro: string) => (
                    <ProItem key={pro}>
                      <Pro height={22} width={22} color="var(--theme-palette-green)" />
                      {pro}
                    </ProItem>
                  ))}
                </List>
              </LeftSide>
            ) : null}
            {cons?.length > 0 ? (
              <RightSide>
                <Title>{t('design.ratingBlock.cons')}</Title>
                <List>
                  {cons?.map((con: string) => (
                    <ConItem key={con}>
                      <Con height={22} width={22} color="var(--theme-palette-red)" />
                      {con}
                    </ConItem>
                  ))}
                </List>
              </RightSide>
            ) : null}
          </ComparativeProsCons>
        </ProsConsWrapper>
      )}
    </ComparativeRow>
  );
};

export default ComparativeCard;
